import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronRight, Loader2, AlertTriangle, Clock, X,
  Briefcase, Bell, FileText, PenLine, ReceiptText,
  CheckCircle2, Hammer,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { buildRoute, ROUTES } from '@/config/routes'
import useAuthStore    from '@/stores/authStore'
import useBookingStore from '@/stores/bookingStore'
import Button from '@/components/ui/Button'

// Returns how many whole days remain until expiresAt
function daysUntil(expiresAt) {
  if (!expiresAt) return null
  const ms = new Date(expiresAt) - Date.now()
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
}

// Derive a single "action required" banner from active bookings
function deriveActionItem(bookings) {
  if (!bookings?.length) return null

  for (const b of bookings) {
    const status = b.status

    // Worker en route or in progress
    if (['assigned', 'en_route', 'in_progress'].includes(status)) {
      return {
        icon:    Hammer,
        color:   'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25',
        label:   'Work in Progress',
        message: 'Your worker is on the job.',
        cta:     'View Project',
        route:   ROUTES.BOOK_HISTORY,
      }
    }

    // Awaiting customer sign-off
    if (status === 'completed' && !b.warranty_expires) {
      return {
        icon:    CheckCircle2,
        color:   'text-green-400 bg-green-500/10 border-green-500/25',
        label:   'Job Complete — Action Required',
        message: 'Accept the completed work and activate your warranty.',
        cta:     'Sign Off',
        route:   ROUTES.BOOK_HISTORY,
      }
    }

    // Pending approval (worker submitted)
    if (status === 'pending_approval') {
      return {
        icon:    Clock,
        color:   'text-pink-400 bg-pink-500/10 border-pink-500/25',
        label:   'Work Done — Pending Verification',
        message: 'Your job is being reviewed by Siglat Konstract.',
        cta:     'View Project',
        route:   ROUTES.BOOK_HISTORY,
      }
    }

    // Has a final proposal ready to sign
    if (b._proposal?.status === 'sent') {
      // No contract yet → needs to sign contract
      if (!b._contract) {
        return {
          icon:    PenLine,
          color:   'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25',
          label:   'Final Proposal Ready',
          message: 'Your detailed proposal is ready. Review and sign the contract.',
          cta:     'View & Sign',
          route:   ROUTES.BOOK_HISTORY,
        }
      }
    }

    // Contract fully signed — needs payment
    if (b._contract?.status === 'fully_signed' && !b._hasPaidFull) {
      return {
        icon:    ReceiptText,
        color:   'text-yellow-400 bg-yellow-500/10 border-yellow-500/25',
        label:   'Payment Required',
        message: 'Your contract is signed. Please submit your payment receipt.',
        cta:     'Upload Receipt',
        route:   ROUTES.BOOK_HISTORY,
      }
    }

    // Contract pending SK signature
    if (b._contract?.status === 'pending_sk') {
      return {
        icon:    Clock,
        color:   'text-blue-400 bg-blue-500/10 border-blue-500/25',
        label:   'Awaiting Siglat Konstract Signature',
        message: 'Your contract is being countersigned.',
        cta:     'View Project',
        route:   ROUTES.BOOK_HISTORY,
      }
    }
  }

  return null
}

export default function BookingEntry() {
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [categories,      setCategories]      = useState([])
  const [loading,         setLoading]         = useState(true)
  const [error,           setError]           = useState(null)
  const [actionItem,      setActionItem]      = useState(null)
  const [activeCount,     setActiveCount]     = useState(0)

  // Pending quote popup (pre-proposal stage)
  const [pendingQuote,    setPendingQuote]    = useState(null)
  const [popupDismissed,  setPopupDismissed]  = useState(false)

  useEffect(() => {
    async function init() {
      // Fetch categories
      const { data: cats, error: catErr } = await supabase
        .from('service_categories')
        .select('id, name, name_fil, icon, description, is_active')
        .order('sort_order', { ascending: true })

      if (catErr) {
        setError('Could not load services. Please try again.')
      } else {
        setCategories(cats ?? [])
      }

      if (profile?.id) {
        // Fetch active bookings (not completed/cancelled)
        const { data: bookings } = await supabase
          .from('bookings')
          .select('id, status, warranty_expires, created_at')
          .eq('customer_id', profile.id)
          .not('status', 'in', '("cancelled")')
          .order('created_at', { ascending: false })
          .limit(10)

        const activeBookings = bookings ?? []
        const nonCompleted   = activeBookings.filter((b) => b.status !== 'completed')
        setActiveCount(nonCompleted.length)

        if (activeBookings.length > 0) {
          const bIds = activeBookings.map((b) => b.id)

          // Parallel: proposals, contracts, payments
          const [{ data: proposals }, { data: contracts }, { data: receipts }] = await Promise.all([
            supabase
              .from('final_proposals')
              .select('id, booking_id, status')
              .in('booking_id', bIds),
            supabase
              .from('contracts')
              .select('id, booking_id, status')
              .in('booking_id', bIds),
            supabase
              .from('payment_receipts')
              .select('id, booking_id, receipt_type, status')
              .in('booking_id', bIds)
              .eq('status', 'verified'),
          ])

          const propMap     = {}
          for (const p of proposals ?? []) propMap[p.booking_id] = p

          const contractMap = {}
          for (const c of contracts ?? []) contractMap[c.booking_id] = c

          const paidSet = new Set((receipts ?? []).map((r) => r.booking_id))

          const enriched = activeBookings.map((b) => ({
            ...b,
            _proposal:   propMap[b.id]     ?? null,
            _contract:   contractMap[b.id] ?? null,
            _hasPaidFull: paidSet.has(b.id),
          }))

          setActionItem(deriveActionItem(enriched))
        }

        // Pending AI quote popup (pre-proposal, still within validity window)
        const { data: quotes } = await supabase
          .from('quotes')
          .select(`
            id, quote_reference, expires_at, booking_id,
            bookings ( id, address, service_categories ( name ) )
          `)
          .eq('status', 'active')
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)

        if (quotes?.length) {
          const q = quotes[0]
          setPendingQuote({
            id:              q.id,
            quote_reference: q.quote_reference,
            expires_at:      q.expires_at,
            booking_id:      q.booking_id,
            category_name:   q.bookings?.service_categories?.name ?? 'Service',
            address:         q.bookings?.address ?? '',
          })
        }
      }

      setLoading(false)
    }

    init()
  }, [profile?.id]) // eslint-disable-line

  const activeCategories = categories.filter((c) => c.is_active)

  function handleContinueProposal() {
    if (!pendingQuote) return
    useBookingStore.getState().setBookingId(pendingQuote.booking_id, null)
    useBookingStore.getState().setQuoteId(pendingQuote.id)
    navigate(ROUTES.BOOK_FINAL_PROPOSAL)
  }

  function handleStartNew() {
    useBookingStore.getState().reset()
    setPendingQuote(null)
    setPopupDismissed(true)
  }

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-3 px-4 text-center">
        <AlertTriangle className="w-8 h-8 text-siglat-red" />
        <p className="text-ink-primary font-semibold">{error}</p>
        <button onClick={() => window.location.reload()} className="text-sm text-siglat-teal hover:underline">
          Retry
        </button>
      </div>
    )
  }

  const showPopup = !!pendingQuote && !popupDismissed
  const daysLeft  = daysUntil(pendingQuote?.expires_at)
  const ActionIcon = actionItem?.icon

  return (
    <div className="min-h-dvh bg-surface-base">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-5 safe-top">
        <div className="page-container flex items-center justify-between">
          <div>
            <p className="text-sm text-ink-muted mb-1">
              Hi, {profile?.full_name?.split(' ')[0] ?? 'there'} 👋
            </p>
            <h1 className="font-display text-2xl font-bold text-ink-primary">
              What do you need fixed?
            </h1>
          </div>
          {/* My Projects button — always visible */}
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="relative flex flex-col items-center gap-0.5 text-ink-muted hover:text-siglat-teal transition-colors"
          >
            <Briefcase className="w-5 h-5" />
            <span className="text-[10px]">Projects</span>
            {activeCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-siglat-teal text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {activeCount > 9 ? '9+' : activeCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="page-container py-5 flex flex-col gap-3">

        {/* ── Action Required banner ─────────────────────────────────────── */}
        {actionItem && (
          <button
            onClick={() => navigate(actionItem.route)}
            className={`w-full flex items-center gap-3 border rounded-xl px-4 py-3.5 text-left transition-colors hover:opacity-90 ${actionItem.color}`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${actionItem.color}`}>
              {ActionIcon && <ActionIcon className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-widest mb-0.5 opacity-80">
                {actionItem.label}
              </p>
              <p className="text-sm font-medium leading-tight">{actionItem.message}</p>
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold shrink-0">
              {actionItem.cta} <ChevronRight className="w-3.5 h-3.5" />
            </div>
          </button>
        )}

        {/* ── Category list ──────────────────────────────────────────────── */}
        <p className="text-xs text-ink-muted uppercase tracking-widest pt-1">Book a Service</p>

        {activeCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => navigate(buildRoute.bookCategory(cat.name.toLowerCase().replace(/\s+/g, '-')))}
            className="w-full flex items-center gap-4 bg-surface-raised border border-surface-border
                       rounded-xl px-4 py-4 text-left hover:border-siglat-teal/50
                       active:scale-[0.98] transition-all"
          >
            <span className="text-3xl w-10 text-center shrink-0">{cat.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-ink-primary leading-tight">{cat.name}</p>
              <p className="text-sm text-ink-muted">{cat.name_fil}</p>
              {cat.description && (
                <p className="text-xs text-ink-muted mt-0.5 truncate">{cat.description}</p>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-ink-muted shrink-0" />
          </button>
        ))}

        {/* ── My Projects shortcut ───────────────────────────────────────── */}
        <button
          onClick={() => navigate(ROUTES.BOOK_HISTORY)}
          className="w-full flex items-center gap-3 bg-surface-raised border border-surface-border rounded-xl px-4 py-3.5 text-left hover:border-siglat-teal/50 transition-all"
        >
          <div className="w-9 h-9 rounded-xl bg-surface-overlay border border-surface-border flex items-center justify-center shrink-0">
            <Briefcase className="w-4 h-4 text-ink-muted" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-ink-primary">My Projects</p>
            <p className="text-xs text-ink-muted">
              {activeCount > 0 ? `${activeCount} active booking${activeCount !== 1 ? 's' : ''}` : 'View your booking history'}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-ink-muted shrink-0" />
        </button>

      </div>

      {/* ── Pending Quote Popup (bottom sheet) ────────────────────────────── */}
      {showPopup && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setPopupDismissed(true)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-surface-raised border-t border-surface-border rounded-t-2xl px-4 pt-5 pb-8 safe-bottom">
            <button
              type="button"
              onClick={() => setPopupDismissed(true)}
              className="absolute top-4 right-4 text-ink-muted hover:text-ink-primary"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="text-sm text-ink-muted mb-1">
              Welcome back, {profile?.full_name?.split(' ')[0] ?? 'there'}!
            </p>
            <p className="font-semibold text-ink-primary mb-4">
              You have a pending estimate.
            </p>

            <div className="bg-surface-overlay border border-surface-border rounded-xl p-4 mb-5">
              <p className="text-siglat-teal font-mono text-sm font-semibold">
                {pendingQuote.quote_reference}
              </p>
              <p className="text-ink-primary text-sm mt-0.5">
                {pendingQuote.category_name}
                {pendingQuote.address ? ' — ' + pendingQuote.address.split(',').pop()?.trim() : ''}
              </p>
              <div className="flex items-center gap-1.5 mt-2 text-ink-muted">
                <Clock className="w-3.5 h-3.5" />
                <span className="text-xs">
                  {daysLeft > 0
                    ? 'Expires in ' + daysLeft + ' day' + (daysLeft === 1 ? '' : 's')
                    : 'Expiring today'}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                fullWidth
                size="lg"
                onClick={handleContinueProposal}
                rightIcon={<ChevronRight className="w-4 h-4" />}
              >
                Continue to Final Proposal
              </Button>
              <Button fullWidth variant="secondary" size="md" onClick={handleStartNew}>
                Start New Service
              </Button>
            </div>
          </div>
        </>
      )}

    </div>
  )
}
